/**
 * GET /v1/athletics-availability/summary — coach/admin roster-availability
 * summary for a single event.
 *
 * Each current STUDENT member of the event's section is bucketed
 * In / Out / Maybe / No-response, with counts. No-response = current canonical
 * `SectionMembership` minus responders, computed server-side (plan U5, R12/R13).
 * Notes are surfaced to coaches/admins only (R14/R15); rendered as plain text.
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-16-003-feat-athletics-availability-rsvp-plan.md (U4/U5/U6).
 */

import { z } from 'zod'
import { AthleticAvailabilityStatusSchema } from '../../schemas/entities/athletic-availability'

export const GetAvailabilitySummaryRequestSchema = z.object({
  /** Public CUID of the athletic game/practice to summarize. */
  gameId: z.string()
})
export type GetAvailabilitySummaryRequest = z.infer<typeof GetAvailabilitySummaryRequestSchema>

/** One roster member's line in the summary. `status` is null for No-response. */
export const AvailabilitySummaryMemberSchema = z.object({
  /** Public CUID of the roster member (school-user). */
  userId: z.string(),
  /** Display name for the roster member. */
  name: z.string().nullable().optional(),
  /** The member's response, or null when they have not responded. */
  status: AthleticAvailabilityStatusSchema.nullable(),
  /** The member's optional note (coach/admin-visible only). */
  note: z.string().nullable().optional()
})
export type AvailabilitySummaryMember = z.infer<typeof AvailabilitySummaryMemberSchema>

/** Bucket counts across the roster. `noResponse` = roster minus responders. */
export const AvailabilitySummaryCountsSchema = z.object({
  in: z.number().int(),
  out: z.number().int(),
  maybe: z.number().int(),
  noResponse: z.number().int(),
  total: z.number().int()
})
export type AvailabilitySummaryCounts = z.infer<typeof AvailabilitySummaryCountsSchema>

export const GetAvailabilitySummaryResponseSchema = z.object({
  data: z.object({
    /** Public CUID of the summarized game/practice. */
    gameId: z.string(),
    counts: AvailabilitySummaryCountsSchema,
    members: z.array(AvailabilitySummaryMemberSchema)
  })
})
export type GetAvailabilitySummaryResponse = z.infer<typeof GetAvailabilitySummaryResponseSchema>
